import { CReading, CEntity, CLabel, CLocation, CStatusIndicator, CUnitFormatter, CElementType, CDevice, IStatusIndicator, TEntity, IBasicElement, IElement, IEvent, IReadingData, READING_TYPE_MAP } from '@ts/ui-models';

import OlFeature from 'ol/Feature';
import OlPoint from 'ol/geom/Point';
import OlLineString from 'ol/geom/LineString';
import { mqttSubscribe, mqttUnsubscribe } from '@components/Utils/_hooks/mqttFunctions';

import { IMapFeature, IRulesetAction } from '@interfaces/maps/Map.interface';

interface IMQTTLocation {
  itemCustomId: string;
  itemId: string;
  locationCustomIds: string[];
  locationIds: string[];
}
interface IMQTTLabels {
  itemCustomId: string;
  itemId: string;
  labels: string[];
}

interface IMQTTStatus {
  itemCustomId: string;
  itemId: string;
  statusIndicators: {
    key: any
  };
}

type TReadingTypeId = string;
export type TTypeCReadings = Record<TReadingTypeId, CReading[]>;
export class CMapElement extends CEntity<IElement | IBasicElement> {
  tEntity: TEntity = 'item';
  observer: CMapObserver | null = null;
  needUpdate: boolean = true; //Reload data
  featureSelected: boolean = false; //Change color icon when selected
  subscribed: boolean = false;

  id: string;
  customId: string;
  elementType: CElementType | null = null;
  layerIDs: string[] = [];

  labels: CLabel[] = [];
  locations: CLocation[] = [];

  readings: TTypeCReadings = {};
  newReadings: TTypeCReadings = {};
  lastPosition: IReadingData | null = null;
  colorInMap: string = '#000';
  events: IEvent[] = [];

  rulesetAction?: IRulesetAction;
  statuses: CStatusIndicator[] = [];

  //InfoSideBar
  attachedDevices?: CDevice[] = [];

  //Map
  mainFeature?: OlFeature<OlPoint>;
  worm: IMapFeature = { markers: [], lines: [] };
  defaultIcon?: string;

  //Formater
  unitFormatter?: CUnitFormatter;

  constructor(item: IElement | IBasicElement, unitFormatter?: CUnitFormatter) {
    super(item);
    this.id = item.id ?? item.logicalId;
    this.customId = item.customId;
    this.elementType = item.elementType ? new CElementType(item.elementType) : null;

    this.statuses = item.currentStatus?.indicators?.map(xi => new CStatusIndicator(xi, unitFormatter)) ?? [];

    this.unitFormatter = unitFormatter;
  }

  private determineIconToShow(isSelected: boolean | undefined, defaultIcon: { color: string; name: string }) {
    const itemAction = this.rulesetAction;
    const itemStatuses = this.statuses;

    if (!itemAction?.data) return defaultIcon;

    const { mapIcon, clickedIcon, mapLiveIcon, clickedLiveIcon } = itemAction.data;

    const noSelectedLiveIcon = itemStatuses?.find(s => s.logicalId === mapLiveIcon?.logicalId)?.svgIcon;
    const selectedLiveIcon = itemStatuses?.find(s => s.logicalId === clickedLiveIcon?.logicalId)?.svgIcon;

    return isSelected && selectedLiveIcon
      ? selectedLiveIcon
      : !isSelected && noSelectedLiveIcon
        ? noSelectedLiveIcon
        : isSelected
          ? clickedIcon ?? mapIcon
          : mapIcon ?? clickedIcon;
  }

  private determineIconName(iconToShow: { name?: string }, defaultName: string): string {
    return iconToShow?.name ?? defaultName;
  }

  private determineIconColor(iconToShow: { color?: string }, defaultColor: string): string {
    return iconToShow?.color ?? defaultColor;
  }

  getIcon(isSelected?: boolean): string {
    const defaultIcon = { color: 'primary', name: this.defaultIcon ?? 'location_on' };

    const iconToShow = this.determineIconToShow(isSelected, defaultIcon);

    return this.determineIconName(iconToShow, defaultIcon.name);
  }
  getIconColor(isSelected?: boolean): string {
    const defaultIcon = { color: 'primary', name: this.defaultIcon ?? 'location_on' };

    const iconToShow = this.determineIconToShow(isSelected, defaultIcon);

    return this.determineIconColor(iconToShow, defaultIcon.color);
  }

  getReadingById(readingTypeId: string, readingId: string): CReading | null {
    if (this.readings[readingTypeId]) {
      return this.readings[readingTypeId].find(r => r.id === readingId) ?? null;
    }
    return null;
  }

  connectMQTT() {
    mqttSubscribe(`${this.getID()}/update`, (t, v) => {
      if (
        t.includes(`reading/${READING_TYPE_MAP.LOCATION_GPS_LAT_LON_COORDINATES}`) ||
        t.includes(`reading/${READING_TYPE_MAP.LOCATION_LOCAL_COORDINATES}`)
      ) {
        if (this.unitFormatter) {
          this.newReadings['locationGPSLatLonCoordinates'] = [
            new CReading('locationGPSLatLonCoordinates', v, this.unitFormatter),
          ];
          this.lastPosition = this.newReadings['locationGPSLatLonCoordinates'][0].realData;
        }
        this.notify();
      } else if (t.includes('locations')) {
        this.needUpdate = true;
        this.notify('updateAllItem');
      } else if (t.includes('status')) {
        const listIndicator: IStatusIndicator[] = Object.values((v as IMQTTStatus).statusIndicators);
        this.statuses = this.statuses.map(s => {
          const newStatus = listIndicator.find(lsi => lsi.indicatorTypeId === s.logicalId);
          return new CStatusIndicator({
            ...s,
            ...newStatus,
          }, this.unitFormatter);
        })

        this.needUpdate = true;
        this.notify();
      } else if (t.includes('labels')) {
        this.needUpdate = true;
        this.notify('updateAllItem');
      } else if (t.includes('events')) {
        this.events.unshift(v);
        this.events = this.events.slice(0, 10);
      }
    });
    this.subscribed = true;
  }
  disconnectMQTT() {
    mqttUnsubscribe(`${this.getID()}/update`);
    this.subscribed = false;
  }
  processNewReadings() {
    Object.entries(this.newReadings).forEach(([readingTypeId, newReadingArray]) => {
      if (this.readings[readingTypeId]) {
        this.readings[readingTypeId] = [...newReadingArray, ...this.readings[readingTypeId]];
      } else {
        this.readings[readingTypeId] = newReadingArray;
      }
      this.newReadings = {};
    });
  }

  removeReadings(readingTypeId: string, idsReadings: string[]) {
    const filterReadings = this.readings[readingTypeId].filter(read => !idsReadings.includes(read.id));

    if (filterReadings.length === 0) {
      this.mainFeature = undefined;
      this.worm = { lines: [], markers: [] };
      this.readings[readingTypeId] = [];
      this.lastPosition = null;
      this.notify('updateFilters');
      this.notify();
      return;
    } else {
      const filterLineWorm: OlFeature<OlLineString>[] = [];
      const filterPointsWorm: OlFeature<OlPoint>[] = [];
      let transformReadigsToRemove: string[] = [];
      this.worm.lines.forEach(line => {
        const transform = idsReadings.map(id => this.getID() + '-line-' + id);
        if (!transform.includes(line.getId() as string)) {
          filterLineWorm.push(line);
        }
        transformReadigsToRemove = transformReadigsToRemove.concat(transform);
      });
      this.worm.markers.forEach(mark => {
        const transform = idsReadings.flatMap(id => [this.getID() + '-point-' + id, this.getID() + '-mid-point-' + id]);
        if (!transform.includes(mark.getId() as string)) {
          filterPointsWorm.push(mark);
        }
        transformReadigsToRemove = transformReadigsToRemove.concat(transform);
      });
      this.readings[readingTypeId] = filterReadings;
      this.worm = { lines: filterLineWorm, markers: filterPointsWorm };
      this.notify('removeReadings', transformReadigsToRemove);
    }
  }

  reset() {
    this.readings = {};
    this.worm = { lines: [], markers: [] };
    this.mainFeature = undefined;
  }

  notify(type: TObserver = 'updateReadings', data?: any) {
    if (this.observer) this.observer.notifyUpdate({ type, updatedItem: this, data });
  }
}

export type TObserver = 'updateReadings' | 'remarkFeature' | 'removeReadings' | 'updateFilters' | 'updateAllItem';
export type TUpdateMap = { type: TObserver; updatedItem: CMapElement; data: any };
export class CMapObserver {
  private observers: ((tUpdateMap: TUpdateMap) => void)[] = [];

  subscribe(callback: (tUpdateMap: TUpdateMap) => void) {
    this.observers.push(callback);
  }

  unsubscribe(callback: (tUpdateMap: TUpdateMap) => void) {
    this.observers = this.observers.filter(cb => cb !== callback);
  }

  notifyUpdate(tUpdateMap: TUpdateMap) {
    this.observers.forEach(callback => callback(tUpdateMap));
  }
}
